import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType, CodeBlock, BlockType, ValidationMessage, ValidationMessageType,
    SettingState,
    SettingOption,
} from '@appblocks/codegeneration/types';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';

export default class CustomFeatureSetting extends BaseFeature {
    constructor() {
        super();
        this.name = 'Custom Feature Setting';
        this.id = 'custom_feature_setting';
        this.singleInstance = false;
        this.settings = this.settings.concat([
            {
                id: 'SETTING_ID',
                name: 'Setting ID',
                settingType: SettingType.STRING,
                required: true,
                default: 'setting_id',
            },
            {
                id: 'NAME',
                name: 'Name',
                settingType: SettingType.STRING,
                required: true,
                default: 'Setting Name',
            },
            {
                id: 'DESCRIPTION',
                name: 'Description',
                settingType: SettingType.STRING,
                required: true,
                default: '',
            },
            {
                id: 'TYPE',
                name: 'Setting Type',
                settingType: SettingType.OPTION,
                required: true,
                default: 'value',
                options: [],
            },
            {
                id: 'REFID',
                name: 'Referenced Feature',
                settingType: SettingType.STRING,
                required: true,
                default: '',
            },
            {
                id: 'FEATURE_CAPABILITY',
                name: 'Feature Capability Requirement',
                settingType: SettingType.STRINGVARIABLE,
                required: true,
                default: '',
            },
            {
                id: 'PIN_REQUIREMENT',
                name: 'Pin Requirement',
                settingType: SettingType.OPTION,
                required: true,
                default: 'GPIO',
                options: [
                    {
                        name: 'GPIO',
                        value: 'GPIO',
                    },
                    {
                        name: 'Interrupt',
                        value: 'INT',
                    },
                    {
                        name: 'PWM',
                        value: 'PWM',
                    },
                    {
                        name: 'ADC',
                        value: 'ADC',
                    },
                    {
                        name: 'TX',
                        value: 'TX',
                    },
                    {
                        name: 'RX',
                        value: 'RX',
                    },
                    {
                        name: 'RTS',
                        value: 'RTS',
                    },
                    {
                        name: 'CTS',
                        value: 'CTS',
                    },
                    {
                        name: 'DTR',
                        value: 'DTR',
                    },
                    {
                        name: 'DSR',
                        value: 'DSR',
                    },
                ],
            },
            {
                id: 'DATA_TYPE',
                name: 'Data Type',
                settingType: SettingType.OPTION,
                required: true,
                default: '',
                options: [
                    {
                        name: 'String',
                        value: 'string',
                    },
                    {
                        name: 'Byte',
                        value: 'byte',
                    },
                    {
                        name: 'Float',
                        value: 'float',
                    },
                    {
                        name: 'Integer',
                        value: 'integer',
                    },
                    {
                        name: 'Boolean',
                        value: 'boolean',
                    },
                    {
                        name: 'DWORD (32-bit Unsigned Integer)',
                        value: 'dword',
                    },
                    {
                        name: 'Long (32 bit Signed Integer)',
                        value: 'long',
                    },
                ],
            },
            {
                id: 'REQUIRED',
                name: 'Required',
                settingType: SettingType.OPTION,
                required: true,
                default: '1',
                options: [
                    {
                        name: 'No',
                        value: '0',
                    },
                    {
                        name: 'Yes',
                        value: '1',
                    },
                ],
            },
            {
                id: 'DEFAULT_VALUE',
                name: 'Default Value',
                settingType: SettingType.STRING,
                required: true,
                default: '',
            },
            {
                id: 'OPTIONS',
                name: 'Dropdown Options',
                settingType: SettingType.ARRAY,
                required: true,
                default: [],
                refId: 'custom_feature_setting_option',
            },
            {
                id: 'STATE',
                name: 'State',
                settingType: SettingType.OPTION,
                required: true,
                default: 'enabled',
                options: [
                    {
                        name: 'Enabled',
                        value: 'enabled',
                    },
                    {
                        name: 'Disabled',
                        value: 'disabled',
                    },
                    {
                        name: 'Hidden',
                        value: 'hidden',
                    },
                ],
            },
        ]);
    }

    getState(settingId: string): SettingState {
        const TYPE = this.getSettingValue('TYPE');
        if (settingId === 'OPTIONS') {
            return TYPE === 'option' ? SettingState.ENABLED : SettingState.HIDDEN;
        }
        if (settingId === 'DEFAULT_VALUE') {
            return TYPE === 'pin' ? SettingState.HIDDEN : SettingState.ENABLED;
        }
        if (settingId === 'REFID') {
            return TYPE === 'array' ? SettingState.ENABLED : SettingState.HIDDEN;
        }
        if (settingId === 'FEATURE_CAPABILITY') {
            return TYPE === 'feature' ? SettingState.ENABLED : SettingState.HIDDEN;
        }
        if (settingId === 'DATA_TYPE') {
            return TYPE !== 'array' ? SettingState.ENABLED : SettingState.HIDDEN;
        }
        if (settingId === 'PIN_REQUIREMENT') {
            return TYPE === 'pin' ? SettingState.ENABLED : SettingState.HIDDEN;
        }
        return super.getState(settingId);
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        if (settingId === 'TYPE') {
            const feature = this.project.getParent(this);
            const featureType = feature.getSettingValue('TYPE');
            let settingTypes = [
                {
                    name: 'Name',
                    value: 'name',
                },
                {
                    name: 'Value',
                    value: 'value',
                },
                {
                    name: 'Dropdown',
                    value: 'option',
                },
                {
                    name: 'Array',
                    value: 'array',
                },
                {
                    name: 'Feature',
                    value: 'feature',
                },
            ];
            if (featureType === 'block') {
                return settingTypes;
            }
            settingTypes = settingTypes.concat([
                {
                    name: 'Pin',
                    value: 'pin',
                },
            ]);
            return settingTypes;
        }
        if (settingId === 'FEATURE_CAPABILITY') {
            return PlatformUtils.getAllCapabilities().map((capability) => {
                return {
                    name: `${capability.name} (${capability.description})`,
                    value: capability.name,
                };
            });
        }
        return super.getSettingOptions(settingId);
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];

        return blocks;
    }
}
